// src/helpers/generateDoc.js
import pdfMake from "pdfmake/build/pdfmake";
import "pdfmake/build/vfs_fonts";
import logo from "../assets/images/logo.png";

pdfMake.vfs = window.pdfMake.vfs;

const loadImageBase64 = (url) =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = function () {
            const canvas = document.createElement("canvas");
            canvas.width = this.width;
            canvas.height = this.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(this, 0, 0);
            resolve(canvas.toDataURL("image/png"));
        };
        img.onerror = reject;
        img.src = url;
    });

const BRAND = {
    primary: "#a60805",      // accent
    primarySoft: "#bfe1ff",  // soft background
    ink: "#0F172A",          // text
    subtle: "#0F172A",       // secondary text
    line: "#E2E8F0",         // separators
    tableHeader: "#F1F5F9",  // header cells
    zebra: "#ffffff",        // odd rows
};

/** Utilities */
const fmtMoney = (n) => {
    const x = Number(n || 0);
    const s = (Math.round((x + Number.EPSILON) * 100) / 100).toFixed(2);
    return s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};
const fmtQty = (n) => {
    const s = String(Number(n ?? 0));
    const fixed = Number(s).toFixed(2);
    return fixed.replace(/\.00$/, "").replace(/(\.\d)0$/, "$1");
};
const toFr = (value) => {
    if (!value) return "";

    // si déjà string type "23-01-2026 14:41" ou "2026-01-23"
    const str = String(value);

    // extraire uniquement la partie date
    const match = str.match(/(\d{4})-(\d{2})-(\d{2})|(\d{2})-(\d{2})-(\d{4})|(\d{2})\/(\d{2})\/(\d{4})/);

    let d;

    if (match) {
        // format YYYY-MM-DD
        if (match[1]) d = new Date(match[1], match[2] - 1, match[3]);
        // format DD-MM-YYYY
        else if (match[4]) d = new Date(match[6], match[5] - 1, match[4]);
        // format DD/MM/YYYY
        else if (match[7]) d = new Date(match[9], match[8] - 1, match[7]);
    } else {
        d = new Date(value);
    }

    if (isNaN(d)) return str;

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();

    return `${day}/${month}/${year}`;
};
function numberToFrenchWords(n) {
    if (n === 0) return "zéro";
    const U = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize"];
    const D = ["", "dix", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante", "quatre-vingt", "quatre-vingt"];
    const H = (x) => (x === 0 ? "" : x === 1 ? "cent" : U[x] + " cent" + (x > 1 ? "s" : ""));
    const under100 = (x) => {
        if (x < 17) return U[x];
        const d = Math.floor(x / 10), u = x % 10;
        if (d === 7 || d === 9) return D[d] + (u === 1 ? " et " : "-") + (u ? U[10 + u] : U[10]);
        if (d === 8 && u === 0) return "quatre-vingts";
        if (u === 1 && d >= 1 && d <= 6) return D[d] + " et un";
        return D[d] + (u ? "-" + U[u] : "");
    };
    const under1000 = (x) => {
        const c = Math.floor(x / 100), r = x % 100;
        if (!c) return under100(r);
        return r ? `${H(c)} ${under100(r)}` : H(c);
    };
    let out = "";
    const m = Math.floor(n / 1_000_000);
    const k = Math.floor((n % 1_000_000) / 1000);
    const r = n % 1000;
    if (m) out += (m === 1 ? "un million" : under1000(m) + " millions");
    if (k) out += (out ? " " : "") + (k === 1 ? "mille" : under1000(k) + " mille");
    if (r) out += (out ? " " : "") + under1000(r);
    return out;
}
const amountToWordsMAD = (n) =>
    numberToFrenchWords(Math.round(n || 0)).toUpperCase() + " DIRHAMS";

/** Map seller (name + subtitle centered header) */
function mapSeller(s) {
    return {
        name: s?.name || "",
        subtitle: s?.subtitle || "",
    };
}

// Build the two footer lines from sellerModel (or seller)
function buildFooterLines(fromSeller) {
    const s = fromSeller || {};
    // line 1: address - phone1[-phone2] - email
    const address = s.address ? String(s.address).replace(/\r?\n/g, ", ").trim() : null;
    const phones = [s.phone1, s.phone2].filter(Boolean).join("-");
    const email = s.email || null;
    const line1 = [s.name].filter(Boolean).join(" - ");
    const line2 = [address].filter(Boolean).join(" - ");
    const line3 = [`Tél: ${phones}` || null, `E-mail: ${email}`].filter(Boolean).join(" - ");

    // line 2: RC: x - Patente: y - IF: z - CNSS: w (only show what exists)
    const regs = [
        s.ice ? `ICE: ${s.ice}` : null,
        s.rc ? `RC: ${s.rc}` : null,
        s.patente ? `Patente: ${s.patente}` : null,
        s.identificationFiscale ? `IF: ${s.identificationFiscale}` : null,
        s.cnss ? `CNSS: ${s.cnss}` : null,
    ].filter(Boolean).join(" - ");

    return { line1, line2, line3, regs };
}


/** Map buyer/doc from Matjar payload */
function mapFromMatjar(doc) {

    const items = (doc.lignes || []).map((l) => {
        const unitTTC = Number(l.prixUnitaireTTC ?? 0);
        const unitHT = Number(l.prixUnitaireHT ?? 0);
        const totalHT = Number(l.montantHT);
        const totalTVA = Number(l.montantTVA);
        const totalTTC = Number(l.montantTTC);
        const sub = [l.articleCode, l.unite, l.remise ? `Remise: ${l.remise}` : null]
            .filter(Boolean).join(" • ");
        return {
            label: l.articleNom || "",
            sublabel: sub || undefined,
            qty: Number(l?.quantiteLivree ?? l?.quantite ?? 0),
            unit: l?.unite || "",
            unitHT, unitTTC, totalHT, totalTVA, totalTTC,
        };
    });

    const buyer = {
        name: doc.clientNom || "",
        lines: [
            //doc.clientAdresse,
            doc.clientIce ? `ICE: ${doc.clientIce}` : null,
            doc.clientTelephone1 ? `Tél: ${doc.clientTelephone1}` : null,
            //doc.clientTelephone2 ? `Tél 2: ${doc.clientTelephone2}` : null,
            //doc.clientEmail ? `Email: ${doc.clientEmail}` : null,
        ].filter(Boolean),
    };

    const meta = {
        number: doc.numero || "",
        dateLivraison: toFr(doc.dateLivraison || doc.dateOperation),
        dateCreation: toFr(doc.createdAt),
        dateOperation: toFr(doc.dateOperation),
        dateFacture: toFr(doc.dateFacture),
        payment: doc.modeReglement || undefined,
        transporteur: doc.transporteurName,
    };

    return { items, buyer, meta };
}

export async function generateDoc(docType, data = {}) {

    const logoBase64 = await loadImageBase64(logo);

    const tMap = {
        devis: "DEVIS",
        bon_commande: "BON DE COMMANDE",
        bon_livraison: "BON DE LIVRAISON",
        facture: "FACTURE",
    };

    const {
        // Either pass matjarDoc + sellerModel …
        matjarDoc,
        sellerModel,
        // …or pass pre-mapped:
        seller,
        buyer,
        meta,
        items,

        // Options
        priceMode,// TTC or HT
        showPrices = docType !== "bon_livraison",
        open = true,
        download = false,
        returnBlobUrl = false,
        contentScale = 1,
        offsetX = 0,
        offsetY = 0,
        reglements = [],
        showPaymentDetails = true,
    } = data;

    const scale = Math.max(0.7, Math.min(1, Number(contentScale || 1)));
    const sx = (value) => Math.round(Number(value || 0) * scale * 100) / 100;

    // Build entities
    const s = mapSeller(sellerModel || seller || {});
    let docBuyer = buyer, docMeta = meta, docItems = items;

    const sellerForFooter = sellerModel || seller || {};
    const { line1: footerLine1, line2: footerLine2, line3: footerLine3, regs: footerLine4 } = buildFooterLines(sellerForFooter);

    if (matjarDoc) {
        const m = mapFromMatjar(matjarDoc);
        docBuyer = m.buyer;
        docMeta = {
            number: m.meta.number,
            dateLivraison: m.meta.dateLivraison,
            dateCreation: m.meta.dateCreation,
            dateOperation: m.meta.dateOperation,
            dateFacture: m.meta.dateFacture,
            payment: m.meta.payment,
        };
        docItems = m.items;
    }

    // Items table rows (TTC by default for display)
    const headerRow = showPrices
        ? [
            { text: "#", alignment: "center", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Article", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Quantité", alignment: "center", style: "th", width: 70, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Unité", alignment: "center", style: "th", width: 60, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },   // ✅ NOUVEAU
            { text: priceMode === 'HT' ? "P.U. HT" : "P.U. TTC", alignment: "right", style: "th", width: 80, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: priceMode === 'HT' ? "Total HT" : "Total TTC", alignment: "right", style: "th", width: 90, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
        ]
        : [
            { text: "#", alignment: "center", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Article", style: "th", fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Quantité", alignment: "center", style: "th", width: 70, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },
            { text: "Unité", alignment: "center", style: "th", width: 60, fillColor: BRAND.tableHeader, margin: [0, 2, 0, 2] },   // ✅ NOUVEAU
        ];

    const bodyRows = (docItems || []).map((it, i) =>
        showPrices
            ? [
                { text: String(i + 1), alignment: "center" },
                [
                    { text: it.label, bold: true, color: BRAND.ink },
                    it.sublabel ? { text: it.sublabel, color: BRAND.subtle, margin: [0, 2, 0, 0], fontSize: 9 } : "",
                ],
                { text: fmtQty(it.qty), alignment: "center" },
                { text: it.unit || "", alignment: "center" },
                { text: priceMode === 'HT' ? fmtMoney(it.unitHT) : fmtMoney(it.unitTTC), alignment: "right" },
                { text: priceMode === 'HT' ? fmtMoney(it.totalHT) : fmtMoney(it.totalTTC), alignment: "right" },
            ]
            : [
                { text: String(i + 1), alignment: "center" },
                [
                    { text: it.label, bold: true, color: BRAND.ink },
                    it.sublabel ? { text: it.sublabel, color: BRAND.subtle, margin: [0, 2, 0, 0], fontSize: 9 } : "",
                ],
                { text: fmtQty(it.qty), alignment: "center" },
                { text: it.unit || "", alignment: "center" },                      // ✅ NOUVEAU
            ]
    );

    const paymentLabel = (mode) => {
        switch (mode) {
            case "CARTE_BANCAIRE":
                return "Carte bancaire";
            case "ESPECES":
                return "Espèces";
            case "CHEQUE":
                return "Chèque";
            case "VIREMENT":
                return "Virement";
            case "TRAITE":
                return "Traite";
            default:
                return mode || "-";
        }
    };

    const widths = showPrices ? [sx(22), "*", sx(70), sx(60), sx(80), sx(90)] : [sx(22), "*", sx(70), sx(60)];

    // Totals (unchanged logic)
    const sum = (arr, f) => (arr || []).reduce((s, it) => s + (Number(f(it)) || 0), 0);
    const grandHT = showPrices ? matjarDoc.montantTotalHT : 0;
    const grandTVA = showPrices ? matjarDoc.montantTotalTaxe : 0;
    const grandTTC = showPrices ? matjarDoc.montantTotalTTC : 0;
    const amountText = amountToWordsMAD(grandTTC);
    const amountInWordsLabel = docType === 'facture'
        ? `Arr\u00eat\u00e9 la pr\u00e9sente ${(tMap[docType] || "document").toLowerCase()} \u00e0 la somme de : ${amountText}`
        : `Arr\u00eat\u00e9 le pr\u00e9sent ${(tMap[docType] || "document").toLowerCase()} \u00e0 la somme de : ${amountText}`;
    const invoiceReglements = (reglements || []).map((reglement) => ({
        reference: reglement?.refReglement || reglement?.reference || reglement?.id || "-",
        montant: Number(reglement?.montantAlloue ?? reglement?.montant ?? reglement?.montantRegle ?? 0),
        mode: reglement?.modeReglement || reglement?.mode,
        piece: reglement?.numeroCheque || reglement?.numeroPiece || reglement?.referencePaiement || "",
        dateReglement: reglement?.dateReglement || reglement?.datePaiement || reglement?.createdAt,
        dateEcheance: reglement?.dateEcheance,
        banque: reglement?.banque || "",
    }));
    const totalPaye = docType === "facture"
        ? (invoiceReglements.reduce((sum, reglement) => sum + reglement.montant, 0) || Number(matjarDoc?.montantRegle || 0))
        : 0;
    const resteAPayer = docType === "facture" ? Math.max(Number(grandTTC || 0) - totalPaye, 0) : 0;
    const paymentDetailsBlock = docType === "facture" && showPaymentDetails && invoiceReglements.length > 0 ? {
        stack: [
            { text: "D\u00e9tails des paiements", bold: true, color: BRAND.ink, margin: [0, sx(8), 0, sx(4)] },
            {
                table: {
                    headerRows: 1,
                    widths: ["*", sx(62), sx(65), sx(62), sx(58), sx(55), sx(62)],
                    body: [
                        [
                            { text: "R\u00e8glement", style: "th", fillColor: BRAND.tableHeader },
                            { text: "Date", style: "th", fillColor: BRAND.tableHeader },
                            { text: "Mode", style: "th", fillColor: BRAND.tableHeader },
                            { text: "Pi\u00e8ce", style: "th", fillColor: BRAND.tableHeader },
                            { text: "\u00c9ch\u00e9ance", style: "th", fillColor: BRAND.tableHeader },
                            { text: "Banque", style: "th", fillColor: BRAND.tableHeader },
                            { text: "Montant", style: "th", alignment: "right", fillColor: BRAND.tableHeader },
                        ],
                        ...invoiceReglements.map((reglement) => [
                            { text: String(reglement.reference), color: BRAND.ink },
                            { text: toFr(reglement.dateReglement) || "-", color: BRAND.ink },
                            { text: paymentLabel(reglement.mode), color: BRAND.ink },
                            { text: reglement.piece || "-", color: BRAND.ink },
                            { text: toFr(reglement.dateEcheance) || "-", color: BRAND.ink },
                            { text: reglement.banque || "-", color: BRAND.ink },
                            { text: fmtMoney(reglement.montant), alignment: "right", bold: true, color: BRAND.ink },
                        ]),
                    ],
                },
                layout: {
                    fillColor: (rowIndex) => (rowIndex === 0 ? BRAND.tableHeader : null),
                    hLineWidth: () => 0.6,
                    vLineWidth: () => 0,
                    hLineColor: () => BRAND.line,
                    paddingTop: () => sx(5),
                    paddingBottom: () => sx(5),
                },
            },
        ],
        margin: [sx(3), sx(2), 0, sx(4)],
    } : null;
    const totalsSummaryBlock = showPrices ? {
        unbreakable: true,
        stack: [
            {
                columns: [
                    { width: "*", text: "" },
                    {
                        width: sx(220),
                        table: {
                            widths: ["*", sx(80)],
                            body:
                                docType === 'facture'
                                    ? [
                                        [
                                            { text: "Sous-total HT", alignment: "right", color: BRAND.subtle },
                                            { text: fmtMoney(grandHT), alignment: "right", color: BRAND.ink },
                                        ],
                                        [
                                            { text: "TVA (20%)", alignment: "right", color: BRAND.subtle },
                                            { text: fmtMoney(grandTVA), alignment: "right", color: BRAND.ink },
                                        ],
                                        [
                                            { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                            { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                                        ],
                                        [
                                            { text: "Pay\u00e9", alignment: "right", color: BRAND.subtle },
                                            { text: fmtMoney(totalPaye), alignment: "right", color: BRAND.ink },
                                        ],
                                        [
                                            { text: "Reste \u00e0 payer", bold: true, alignment: "right", color: BRAND.ink },
                                            { text: fmtMoney(resteAPayer), bold: true, alignment: "right", color: resteAPayer > 0 ? "#b91c1c" : "#15803d" },
                                        ],
                                    ]
                                    : (docType === 'devis' || docType === 'bon_commande')
                                        ? priceMode === 'HT'
                                            ? [
                                                [
                                                    { text: "Total HT", bold: true, alignment: "right", color: BRAND.ink },
                                                    { text: fmtMoney(grandHT), bold: true, alignment: "right", color: BRAND.ink },
                                                ],
                                            ]
                                            : [
                                                [
                                                    { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                                    { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                                                ],
                                            ]
                                        : [
                                            [
                                                { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                                { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                                            ],
                                        ],
                        },
                        layout: {
                            fillColor: () => BRAND.primarySoft,
                            hLineWidth: () => 0,
                            vLineWidth: () => 0,
                            paddingTop: () => sx(10),
                            paddingBottom: () => sx(10),
                            paddingLeft: () => sx(10),
                            paddingRight: () => sx(10),
                        },
                        margin: [0, sx(14), 0, sx(8)],
                    },
                ],
            },
            (matjarDoc?.modeReglement || docMeta?.payment)
                ? {
                    text: `Mode de paiement : ${paymentLabel(matjarDoc?.modeReglement || docMeta?.payment)}`,
                    margin: [0, 12, 0, 2],
                    bold: true,
                    color: BRAND.ink,
                }
                : null,
            {
                text: docType === 'facture'
                    ? `Arrêté la présente ${(tMap[docType] || "document").toLowerCase()} à la somme de : ${amountText}`
                    : `Arrêté le présent ${(tMap[docType] || "document").toLowerCase()} à la somme de : ${amountText}`,
                text: amountInWordsLabel,
                color: BRAND.subtle,
                    fontSize: sx(9),
                    margin: [0, sx(6), 0, 0],
            },
        ].filter(Boolean),
    } : null;

    const CARD_W = sx(250);
    const PADDING_X = sx(10);
    const PADDING_Y = sx(10);
    const RADIUS = sx(8);
    const MIN_CARD_H = sx(100);

    // compute a safe shared height (simple & stable)
    const leftRows = 1 + 3; // title + 3 lines (Numéro, Date, Paiement)
    const rightRows = 1 + 1 + (docBuyer?.lines?.length || 0); // title + name + lines
    const LINE_H = sx(14);
    const CARD_H = Math.max(MIN_CARD_H, 20 + (Math.max(leftRows, rightRows) * LINE_H));

    const stroke = '#000000';
    const fill = '#FFFFFF';
    const dd = {
        pageMargins: [sx(40), sx(10), sx(40), sx(84)],
        content: [{
            margin: [Number(offsetX || 0), Number(offsetY || 0), 0, 0],
            stack: [
            // TOP-CENTER SELLER NAME + SUBTITLE
            {
                columns: [
                    // LEFT — LOGO
                    {
                        width: "*",
                        image: logoBase64,
                        fit: [sx(150), sx(110)],
                        alignment: "left",
                    },

                    // RIGHT — DOCUMENT META (SLIGHTLY LOWER)
                    {
                        width: sx(170),
                        margin: [0, sx(50), 0, 0],
                        stack: [
                            {
                                text: tMap[docType] || "DOCUMENT",
                                style: "docTitle",
                                alignment: "left",
                            },
                            {
                                text: `N° ${docMeta?.number || "-"}`,
                                bold: true,
                                alignment: "left",
                            },
                            ...(docType === "bon_livraison"
                                ? [
                                    {
                                        text: `Date livraison : ${docMeta?.dateLivraison || "-"}`,
                                        alignment: "left",
                                        color: BRAND.subtle,
                                    },
                                    {
                                        text: `Date création : ${docMeta?.dateCreation || "-"}`,
                                        alignment: "left",
                                        color: BRAND.subtle,
                                    },
                                ]
                                : docType === "facture" ? [
                                    {
                                        text: `Date facturation: ${docMeta?.dateFacture || "-"}`,
                                        alignment: "left",
                                        color: BRAND.subtle,
                                    },
                                ] : [
                                    {
                                        text: `Date : ${docMeta?.dateLivraison || docMeta?.dateCreation || "-"}`,
                                        alignment: "left",
                                        color: BRAND.subtle,
                                    },
                                ]),
                        ],
                    },
                ],
                margin: [0, 0, 0, 6],
            },



            // Separator
            { canvas: [{ type: "line", x1: 0, y1: 0, x2: sx(515), y2: 0, lineWidth: 1, lineColor: BRAND.line }], margin: [0, 0, 0, 0] },

            // TWO CARDS BELOW: LEFT (Doc data) | RIGHT (Buyer)
            // === TWO CARDS BELOW (equal height, black border, white background, rounded corners, gap) ===
            {
                table: {
                    widths: ['*', sx(12), '*'],
                    body: [[
                        // LEFT CARD
                        {
                            stack: [
                                // 1) draw rounded rect
                                { canvas: [{ type: 'rect', x: 0, y: 0, w: CARD_W, h: CARD_H, r: RADIUS, lineColor: stroke, lineWidth: 1, color: fill }] },
                                // 2) overlay content INTO the rect using negative top margin
                                {
                                    margin: [PADDING_X, -(CARD_H - PADDING_Y) /* pull up */, PADDING_X, 0],
                                    stack: [
                                        { text: "Société", bold: true, margin: [0, 0, 0, 6] },
                                        { text: sellerForFooter.name || "", bold: true },
                                        sellerForFooter.address ? { text: sellerForFooter.address } : null,
                                        sellerForFooter.phone1 ? { text: `Tél : ${sellerForFooter.phone1}` } : null,
                                        sellerForFooter.email ? { text: `Email : ${sellerForFooter.email}` } : null,

                                        // sellerForFooter.ice ? { text: `ICE : ${sellerForFooter.ice}` } : null,
                                        // sellerForFooter.rc ? { text: `RC : ${sellerForFooter.rc}` } : null,
                                        // sellerForFooter.identificationFiscale
                                        //   ? { text: `IF : ${sellerForFooter.identificationFiscale}` }
                                        //   : null,
                                        // sellerForFooter.cnss ? { text: `CNSS : ${sellerForFooter.cnss}` } : null,
                                    ].filter(Boolean)
                                },
                                // 3) keep cell total height equal to CARD_H
                                { text: '', margin: [0, sx(15), 0, 0] }
                            ],
                        },

                        // GAP
                        { text: '', border: [false, false, false, false] },

                        // RIGHT CARD
                        {
                            stack: [
                                { canvas: [{ type: 'rect', x: 0, y: 0, w: CARD_W, h: CARD_H, r: RADIUS, lineColor: stroke, lineWidth: 1, color: fill }] },
                                {
                                    margin: [PADDING_X, -(CARD_H - PADDING_Y), PADDING_X, 0],
                                    stack: [
                                        { text: (docType === "bon_livraison" ? "Livrer à" : "Facturer à"), bold: true, margin: [0, 0, 0, 6], color: BRAND.ink },
                                        { text: (docBuyer?.name || ""), bold: true, color: BRAND.ink, margin: [0, 0, 0, 2] },
                                        ...((docBuyer?.lines || []).map((l) => ({ text: l, color: BRAND.subtle }))),
                                    ]
                                },
                                { text: '', margin: [0, sx(15), 0, 0] }
                            ],
                        },
                    ]],
                },
                // wrapper table shows no lines; rounded borders are from canvas
                layout: {
                    defaultBorder: false,
                    hLineWidth: () => 0,
                    vLineWidth: () => 0,
                    paddingTop: () => 0,
                    paddingBottom: () => 0,
                    paddingLeft: () => 0,
                    paddingRight: () => 0,
                },
                margin: [0, sx(10), 0, 1],
            },

            // ITEMS TABLE
            {
                table: {
                    headerRows: 1,
                    widths,
                    body: [headerRow, ...bodyRows],
                },
                layout: {
                    fillColor: (rowIndex) => (rowIndex === 0 ? BRAND.tableHeader : rowIndex % 2 === 0 ? null : BRAND.zebra),
                    hLineWidth: () => 0.6,
                    vLineWidth: () => 0,
                    hLineColor: () => BRAND.line,
                    paddingTop: () => sx(7),
                    paddingBottom: () => sx(7),
                },
                margin: [sx(3), sx(40), 0, 0],
            },

            totalsSummaryBlock,
            ...(paymentDetailsBlock ? [paymentDetailsBlock] : []),

            // === REST OF DOCUMENT UNCHANGED ===

            // Totals card (same visual as before)
            (false && showPrices ? {
                columns: [
                    { width: "*", text: "" },
                    {
                        width: 220,
                        table: {
                            widths: ["*", 80],
                            body:
                                docType === 'facture'
                                    ? [
                                        [
                                            { text: "Sous-total HT", alignment: "right", color: BRAND.subtle },
                                            { text: fmtMoney(grandHT), alignment: "right", color: BRAND.ink },
                                        ],
                                        [
                                            { text: "TVA (20%)", alignment: "right", color: BRAND.subtle },
                                            { text: fmtMoney(grandTVA), alignment: "right", color: BRAND.ink },
                                        ],
                                        [
                                            { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                            { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                                        ],
                                    ]

                                    : (docType === 'devis' || docType === 'bon_commande')
                                        ? priceMode === 'HT'
                                            ? [
                                                [
                                                    { text: "Total HT", bold: true, alignment: "right", color: BRAND.ink },
                                                    { text: fmtMoney(grandHT), bold: true, alignment: "right", color: BRAND.ink },
                                                ],
                                            ]
                                            : [
                                                [
                                                    { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                                    { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                                                ],
                                            ]

                                        : [
                                            [
                                                { text: "Total TTC", bold: true, alignment: "right", color: BRAND.ink },
                                                { text: fmtMoney(grandTTC), bold: true, alignment: "right", color: BRAND.ink },
                                            ],
                                        ],
                        },
                        layout: {
                            fillColor: () => BRAND.primarySoft,
                            hLineWidth: () => 0,
                            vLineWidth: () => 0,
                            paddingTop: () => 10,
                            paddingBottom: () => 10,
                            paddingLeft: () => 10,
                            paddingRight: () => 10,
                        },
                        margin: [0, 14, 0, 8],
                    },
                ],
            } : null),

            (false && (matjarDoc?.modeReglement || docMeta?.payment))
                ? {
                    text: `Mode de paiement : ${paymentLabel(matjarDoc?.modeReglement || docMeta?.payment)}`,
                    margin: [0, 12, 0, 2],
                    bold: true,
                    color: BRAND.ink,
                }
                : null,

            // Amount in words (kept)
            false && showPrices ? {
                text: docType === 'facture' ? 
                    `Arrêté la présente ${(tMap[docType] || "document").toLowerCase()} à la somme de : ${amountText}` : 
                    `Arrêté le présent ${(tMap[docType] || "document").toLowerCase()} à la somme de : ${amountText}`,
                color: BRAND.subtle,
                fontSize: 9,
                margin: [0, 6, 0, 0],
            } : null,
        ].filter(Boolean),
        }],

        // Footer (simple, keep or replace with your previous one)
        footer: function (currentPage, pageCount) {
            return {
                margin: [40, 0, 40, 20],
                stack: [
                    // thin separator
                    { canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5, lineColor: '#000' }], margin: [0, 4, 0, 6] },

                    // centered company info (from sellerModel)
                    {
                        alignment: 'center',
                        stack: [
                            { text: footerLine1 || "", fontSize: 8, color: '#333' },
                            { text: footerLine2 || "", fontSize: 8, color: '#333', margin: [0, 2, 0, 0] },
                            { text: footerLine3 || "", fontSize: 8, color: '#333', margin: [0, 2, 0, 0] },
                            { text: footerLine4 || "", fontSize: 8, color: '#333', margin: [0, 2, 0, 0] },
                        ],
                    },

                    // (optional) page number, keep or remove as you wish
                    {
                        columns: [
                            { width: '*', text: '' },
                            { width: 'auto', alignment: 'right', text: `Page ${currentPage} / ${pageCount}`, fontSize: 8, color: '#888' },
                        ],
                        margin: [0, 4, 0, 0],
                    },
                ],
            };
        },

        styles: {
            sellerTopName: { fontSize: sx(24), bold: true, color: "#074e0dff" },
            sellerTopSub: { fontSize: sx(10), color: "#0b5e12ff" },
            th: { bold: true },
            footerLine: { fontSize: 8, color: "#666" },
            footerPage: { fontSize: 8, color: "#888" },
            docTitle: {
                fontSize: sx(18),
                bold: true,
                color: BRAND.primary,
            },
        },
        defaultStyle: { fontSize: sx(10), },
    };

    const fileName = `${(tMap[docType] || "document")}_${docMetaSafe(docMeta).number}.pdf`;
    if (returnBlobUrl) {
        return new Promise((resolve) => {
            pdfMake.createPdf(dd).getBlob((blob) => {
                resolve(URL.createObjectURL(blob));
            });
        });
    }
    if (download) return pdfMake.createPdf(dd).download(fileName);
    if (open) {
        return pdfMake.createPdf(dd).getBlob((blob) => {
            const blobUrl = URL.createObjectURL(blob);
            const win = window.open(blobUrl, "_blank");

            // Keep the URL alive long enough for the browser PDF viewer to load.
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);

            if (!win) {
                const link = document.createElement("a");
                link.href = blobUrl;
                link.target = "_blank";
                link.rel = "noopener noreferrer";
                link.download = fileName;
                link.click();
            }
        });
    }
    return dd;

    function docMetaSafe(m) {
        return { number: (m?.number || "").replace(/[^\w/-]/g, "") };
    }
}
